"use client";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/common/Loader";
import { getCampaigns, getCampaignRoiTransactions } from "@/services";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  TCampaignModelJSON,
  TCampaignROITransactionsRequest,
  TCampaignROITransactionsResponse
} from "@shoutout-labs/market_buzz_crm_types";
import {
  Calendar,
  Users,
  Send,
  Target,
  ShoppingCart,
  DollarSign,
  Eye,
  TrendingUp
} from "lucide-react";
import { ROIMetricCard } from "./ROIMetricCard";
import { useNavigate } from "react-router";
import { getDateRange } from "@/lib/date-utils";
import { Utility } from "@/utility/Utility";
import { FeatureGuard } from "@/components/permissions/FeatureGuard";
import { usePermissions } from "@/hooks/usePermissions";
import FeatureUtils from "@/utility/FeatureUtils";

const statusMap: Record<string, string> = {
  COMPLETED: "Completed",
  SUCCESS: "Success",
  SCHEDULED: "Scheduled",
  DRAFT: "PROCESSING",
  FAILED: "Failed",
  RUNNING: "Running"
};

function LastCampaignROIContent() {
  const navigate = useNavigate();

  const {
    data: campaignData,
    isLoading: isCampaignLoading,
    isError: isCampaignError
  } = useQuery<
    TCampaignModelJSON | null,
    Error,
    TCampaignModelJSON | null,
    [string]
  >({
    queryKey: ["latest-campaign"],
    queryFn: async () => {
      const res = await getCampaigns({ limit: 1, skip: 0 });
      return (res.items?.[0] as TCampaignModelJSON | undefined) ?? null;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const { data: roiData, isLoading: isROILoading } = useQuery({
    queryKey: ["campaign-roi", campaignData?.id],
    queryFn: async () => {
      if (!campaignData?.id) return undefined;

      const req: TCampaignROITransactionsRequest = {
        campaignId: campaignData.id,
        fromDate: getDateRange(campaignData).fromDate
      };
      return getCampaignRoiTransactions(req);
    },
    enabled: !!campaignData?.id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000
  });

  const handleViewReport = () => {
    if (campaignData?.id) {
      navigate(`/campaigns/sms/${campaignData.id}`);
    }
  };

  const roi: TCampaignROITransactionsResponse[0] | undefined = Array.isArray(
    roiData
  )
    ? roiData[0]
    : undefined;

  if (isCampaignLoading || isROILoading) return <Loader />;
  if (isCampaignError || !campaignData) {
    return (
      <Card className="p-6 mb-4 flex flex-col items-center justify-center text-center">
        <h2 className="text-lg font-semibold mb-2">No Campaigns Yet</h2>
        <p className="text-sm text-muted-foreground mb-4 max-w-md">
          🚀 Get started by launching your first SMS campaign! Engage your
          customers, track performance, and boost conversions with targeted
          messages.
        </p>
        <Button
          onClick={() => navigate("/campaigns/broadcast/")}
          className="mt-2"
        >
          Start Your First Campaign
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-6 mb-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-blue-500" />
            <span className="font-medium text-lg">Last Campaign ROI</span>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={handleViewReport}
          >
            <Eye className="h-4 w-4" />
            View Full Report
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-4 space-y-3">
          <div>
            <span className="font-semibold mr-2">{campaignData.name}</span>
            <Badge variant="secondary">
              {statusMap[campaignData.status] || campaignData.status}
            </Badge>
          </div>
          <div className="text-sm text-gray-500 flex items-center gap-1">
            <Calendar className="h-4 w-4 mr-1 text-blue-500" />
            Launched:{" "}
            {campaignData.createdOn
              ? format(new Date(campaignData.createdOn), "MMM dd, yyyy")
              : "-"}
          </div>
          <div className="text-sm text-gray-700 bg-gray-50 rounded p-3">
            "{campaignData.content}"
          </div>
        </div>
        <div className="lg:col-span-8">
          <div className="flex flex-wrap gap-2">
            <ROIMetricCard
              icon={<Users className="h-5 w-5" />}
              label="Total Customers"
              value={campaignData.report?.customersCount ?? 0}
              tooltipText="The total number of customers targeted in this campaign."
            />
            <ROIMetricCard
              icon={<Send className="h-5 w-5" />}
              label="Sent Count"
              value={campaignData.report?.sentMessageCount ?? 0}
              tooltipText="The number of messages successfully sent."
            />
            <ROIMetricCard
              icon={<Target className="h-5 w-5" />}
              label="Triggered Members"
              value={campaignData.report?.processedMessageCount ?? 0}
              tooltipText="Number of members who engaged with the campaign."
            />
            <ROIMetricCard
              icon={<ShoppingCart className="h-5 w-5" />}
              label="Triggered Transactions"
              value={roi?.totaltransactionCount ?? 0}
              tooltipText="Number of transactions made by campaign recipients."
            />
            <ROIMetricCard
              icon={<DollarSign className="h-5 w-5" />}
              label="Triggered Sales"
              value={
                roi?.totalSales
                  ? Utility.formatCurrency(roi.totalSales)
                  : "$0.00"
              }
              tooltipText="Total sales value from campaign recipients."
            />
          </div>
        </div>
      </div>
    </Card>
  );
}

export function LastCampaignROI() {
  const { permissions } = usePermissions();
  const isCRM = FeatureUtils.isCRMEnabled(permissions);

  // Show for CRM users regardless of feature permission (as fallback)
  if (isCRM) {
    return <LastCampaignROIContent />;
  }

  // For non-CRM users, use feature guard
  return (
    <FeatureGuard featureKey="DASHBAORD_CAMPAIGN_ROI" hideIfNoAccess={true}>
      <LastCampaignROIContent />
    </FeatureGuard>
  );
}
